import { useSyncExternalStore } from 'react';

const POLL_INTERVAL_MS = 100;

export const isPlacesReady = (): boolean => Boolean(window.google?.maps?.places);

const subscribers = new Set<() => void>();
let poll: ReturnType<typeof setInterval> | undefined;

const stopPolling = () => {
  if (poll) {
    clearInterval(poll);
    poll = undefined;
  }
};

const startPolling = () => {
  if (poll || isPlacesReady()) {
    return;
  }

  poll = setInterval(() => {
    if (!isPlacesReady()) {
      return;
    }

    stopPolling();
    for (const notify of subscribers) {
      notify();
    }
  }, POLL_INTERVAL_MS);
};

/**
 * One interval for the whole page rather than one per input. A form can render
 * several address fields, and if Places is blocked or never loads they would each
 * poll forever.
 */
const subscribe = (notify: () => void) => {
  subscribers.add(notify);
  startPolling();

  return () => {
    subscribers.delete(notify);
    if (subscribers.size === 0) {
      stopPolling();
    }
  };
};

/**
 * The Maps script is loaded with `async defer` and a one-shot `callback=`, so a
 * subscriber that mounts after the callback fires never hears about it, and a
 * subscriber that registers before `places` is attached never hears about it
 * either. Polling is indifferent to both orderings.
 */
export const useGooglePlacesReady = (): boolean => useSyncExternalStore(subscribe, isPlacesReady, isPlacesReady);
